import { TwoWalletEventError } from './error';
import { checkoutRedirectsFor, cleanApiBaseUrl } from './url';
import {
  MAX_TICKET_RECIPIENTS,
  type CheckoutResult,
  type PreflightResult,
  type RechargeCheckoutInput,
  type RechargeLookupResult,
  type RechargeSelection,
  type RequestOptions,
  type StorefrontCatalog,
  type StorefrontClient,
  type StorefrontClientConfiguration,
  type StorefrontContext,
  type TicketRecipient,
} from './types';

const VALID_ENVIRONMENTS = new Set(['test', 'live']);

interface RequestSettings {
  method?: 'GET' | 'POST';
  body?: unknown;
  signal?: AbortSignal;
}

interface ApiErrorBody {
  message?: string | string[];
  [key: string]: unknown;
}

function normalizeQuantity(value: number | undefined): number {
  const quantity = value ?? 1;
  if (!Number.isSafeInteger(quantity) || quantity < 1) {
    throw new TypeError('quantity must be a positive whole number.');
  }
  return quantity;
}

function ticketPayload(
  context: StorefrontContext,
  recipients: TicketRecipient[],
): StorefrontContext & { recipients: TicketRecipient[] } {
  if (
    !Array.isArray(recipients) ||
    recipients.length < 1 ||
    recipients.length > MAX_TICKET_RECIPIENTS
  ) {
    throw new TypeError('recipients must contain between 1 and 25 tickets.');
  }
  return { ...context, recipients };
}

function rechargePayload(
  context: StorefrontContext,
  selection: RechargeSelection,
) {
  return {
    ...context,
    email: String(selection.email ?? '').trim(),
    selectionToken: selection.selectionToken,
    priceMappingId: selection.priceMappingId,
    quantity: normalizeQuantity(selection.quantity),
  };
}

function messageFrom(body: ApiErrorBody): string {
  if (Array.isArray(body.message)) return body.message.join(' ');
  if (typeof body.message === 'string') return body.message;
  return 'No se pudo continuar. Intente de nuevo.';
}

export function createClient(
  configuration: StorefrontClientConfiguration,
): StorefrontClient {
  const apiBaseUrl = cleanApiBaseUrl(configuration.apiBaseUrl);
  const templateId = Number(configuration.templateId);
  const environment = configuration.environment ?? 'test';

  if (!Number.isSafeInteger(templateId) || templateId < 1) {
    throw new TypeError('templateId must be a positive integer.');
  }
  if (!VALID_ENVIRONMENTS.has(environment)) {
    throw new TypeError('environment must be test or live.');
  }

  const context: Readonly<StorefrontContext> = Object.freeze({
    templateId,
    environment,
  });

  async function request<ResponseBody>(
    path: string,
    settings: RequestSettings = {},
  ): Promise<ResponseBody> {
    let response: Response;
    try {
      const init: RequestInit = {
        method: settings.method ?? 'GET',
        credentials: 'omit',
        ...(settings.body !== undefined
          ? {
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(settings.body),
            }
          : {}),
        ...(settings.signal ? { signal: settings.signal } : {}),
      };
      response = await globalThis.fetch(
        `${apiBaseUrl}/event-payments/public/${path}`,
        init,
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      throw new TwoWalletEventError(
        'No se pudo conectar. Revise su conexión e intente de nuevo.',
      );
    }

    const body = (await response.json().catch(() => ({}))) as ResponseBody &
      ApiErrorBody;
    if (!response.ok) {
      throw new TwoWalletEventError(messageFrom(body), response.status, body);
    }
    return body;
  }

  const post = <ResponseBody>(
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ) =>
    request<ResponseBody>(path, {
      method: 'POST',
      body,
      ...(signal ? { signal } : {}),
    });

  const client: StorefrontClient = {
    context,

    getCatalog(options?: RequestOptions) {
      const query = new URLSearchParams({ environment });
      return request<StorefrontCatalog>(
        `templates/${templateId}/catalog?${query.toString()}`,
        options?.signal ? { signal: options.signal } : {},
      );
    },

    preflightTickets(recipients, options) {
      return post<PreflightResult>(
        'ticket-purchases/preflight',
        ticketPayload(context, recipients),
        options?.signal,
      );
    },

    createTicketCheckout(input, options) {
      const redirects =
        input.redirects ?? checkoutRedirectsFor(input.returnUrl);
      return post<CheckoutResult>(
        'ticket-purchases/checkout',
        {
          ...ticketPayload(context, input.recipients),
          ...redirects,
          ...(input.buyerEmail ? { buyerEmail: input.buyerEmail } : {}),
        },
        options?.signal,
      );
    },

    async startTicketCheckout(input, options) {
      await client.preflightTickets(input.recipients, options);
      return client.createTicketCheckout(input, options);
    },

    lookupRecharge(email, options) {
      return post<RechargeLookupResult>(
        'recharges/lookup',
        { ...context, email: String(email ?? '').trim() },
        options?.signal,
      );
    },

    preflightRecharge(selection, options) {
      return post<PreflightResult>(
        'recharges/preflight',
        rechargePayload(context, selection),
        options?.signal,
      );
    },

    createRechargeCheckout(input: RechargeCheckoutInput, options) {
      const redirects =
        input.redirects ?? checkoutRedirectsFor(input.returnUrl);
      return post<CheckoutResult>(
        'recharges/checkout',
        { ...rechargePayload(context, input), ...redirects },
        options?.signal,
      );
    },

    async startRechargeCheckout(input, options) {
      await client.preflightRecharge(input, options);
      return client.createRechargeCheckout(input, options);
    },
  };

  return Object.freeze(client);
}
